// Дерево роутов, code-based (app.md §6).
//
// File-based routing (@tanstack/router-plugin, src/routes/) не используем: роуты
// описываются в коде, роль и action живут в мете, дерево одно на все роли.
//
// Структура дерева фиксирует модель доступа:
//   rootRoute
//   ├── публичные: /login, /invite/$token, /invite   — без сессии
//   └── appLayoutRoute (беспутевой) — beforeLoad: requireAuth
//       └── все остальные экраны
//
// Проверка сессии стоит в ОДНОМ месте — в beforeLoad app-layout, а не в каждом роуте.
// Роль проверяется по staticData роута внутри layout-компонента и при отказе рисует
// ForbiddenPage, а не молчаливый редирект.
//
// Дублировать дерево по ролям запрещено: одна страница — один path. Роль решает,
// что на странице показать, а не по какому адресу её открыть.

import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router';

import { hasServerAddress } from '@/shared/api';
import type { SessionEndReason } from '@/shared/session';

import { AppLayout } from './AppLayout';
import { requireAuth } from './guards';
import { RootLayout } from './RootLayout';
import { StartPage } from './StartPage';

/**
 * Контекст роутера. QueryClient в контексте нужен, чтобы loader'ы роутов могли
 * префетчить данные (ensureQueryData) без импорта синглтона по месту.
 */
export type RouterContext = {
  readonly queryClient: QueryClient;
};

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  // Адрес сервера сети не вшит в сборку — до его ввода приложению некуда ходить
  // (Фаза 4). Проверка стоит здесь, а не в каждом роуте: `beforeLoad` корня
  // выполняется для всех путей, включая публичные `/login` и `/invite`. Синхронно
  // и без сети — `hasServerAddress` читает копию в памяти, поднятую в bootstrap.
  beforeLoad: ({ location }) => {
    if (!hasServerAddress() && location.pathname !== '/setup') {
      throw redirect({ to: '/setup' });
    }
  },
});

// --- Публичные маршруты -------------------------------------------------------------

/**
 * Адрес сервера сети. Публичный и вне защищённого поддерева: экран показывается
 * до какого-либо входа, на новом устройстве.
 */
export const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/setup',
  component: lazyRouteComponent(() => import('./pages/SetupRoute'), 'SetupRoute'),
});

/** Параметры входа: куда человек шёл и почему оказался на входе. Оба необязательны. */
export type LoginSearch = {
  readonly redirect?: string;
  /** Сервер отказал сессии (B-034) — экран входа объясняет, что случилось. */
  readonly reason?: SessionEndReason;
};

/** Единый вход. Роль определяет стартовый экран, а не префикс URL (app.md §6). */
export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  // Адресная строка — ввод извне: неизвестная причина отбрасывается, а не рисуется.
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    ...(typeof search.redirect === 'string' ? { redirect: search.redirect } : {}),
    ...(search.reason === 'session_revoked' || search.reason === 'session_expired'
      ? { reason: search.reason }
      : {}),
  }),
  component: lazyRouteComponent(() => import('./pages/LoginRoute'), 'LoginRoute'),
});

/** Установка пароля по ссылке — инвайт и восстановление, экран один. */
export const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: lazyRouteComponent(() => import('./pages/InviteRoute'), 'InviteRoute'),
});

/**
 * Тот же экран после того, как токен убран из адресной строки.
 *
 * Маршрут без параметра существует ровно затем, чтобы сырой ключ от кабинета
 * не оставался в истории браузера и не уезжал в Referer и телеметрию.
 */
export const inviteCapturedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite',
  component: lazyRouteComponent(() => import('./pages/InviteRoute'), 'InviteRoute'),
});

// --- Защищённое поддерево -----------------------------------------------------------

/**
 * Беспутевой layout: единственное место, где проверяется сессия.
 *
 * `beforeLoad` синхронный и в сеть не ходит — см. пояснение в guards.ts.
 */
export const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: ({ location }) => {
    requireAuth(location.pathname);
  },
  component: AppLayout,
});

export const startRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  component: StartPage,
});

/**
 * Меню точки: то, по чему печатают. Правка — не всем, но и ЧТЕНИЕ не всем.
 *
 * Прежний комментарий гласил «видно всем ролям», и `allowedRoles` тут не было
 * вовсе. Написано это было до T-019, которым сотрудник стал жить только в киоске;
 * после T-019 утверждение перестало быть верным, а охраны как не было, так и нет.
 *
 * Что из этого выходило: семь маршрутов кабинета из восьми показывали повару экран
 * отказа, восьмой пускал. Внутри — плотная менеджерская таблица в киоск-оболочке,
 * ровно то, что T-019 у него и убирал («compact-строки менеджера на кухонной
 * дистанции не читаются»), да ещё и без пути назад: кабинетной навигации у него нет.
 *
 * Утечки не было: те же продукты повар видит на экране печати, это его рабочий
 * список, и кнопок правки на странице ему не рисовали. Чинится не доступ к ДАННЫМ,
 * а доступ к ЭКРАНУ.
 */
export const locationMenuRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/catalog/menu',
  component: lazyRouteComponent(() => import('./pages/LocationMenuRoute'), 'LocationMenuRoute'),
  staticData: {
    allowedRoles: ['client_admin', 'location_manager'],
  },
});

/** Родительский каталог сети: ведёт админ сети, платформе видно всё. */
export const masterCatalogRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/catalog/master',
  component: lazyRouteComponent(() => import('./pages/MasterCatalogRoute'), 'MasterCatalogRoute'),
  staticData: {
    // Менеджер точки сюда не ходит: родительский каталог — уровень сети.
    // Скрытие пункта меню это НЕ заменяет — нужны оба (app.md §7).
    allowedRoles: ['platform_admin', 'client_admin'],
  },
});

/**
 * Журнал печати — вторая половина Модуля Б.
 *
 * Дашборд отвечает «что горит сейчас», журнал — «что здесь произошло».
 * `employee` полного журнала не ведёт: разбор инцидентов не его работа.
 */
export const journalRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/journal',
  component: lazyRouteComponent(() => import('./pages/JournalRoute'), 'JournalRoute'),
  staticData: {
    allowedRoles: ['client_admin', 'location_manager'],
  },
});

/**
 * Статистика сети за период (T-057): напечатано, списано, аннулировано, просрочено.
 *
 * Админ сети — вся сеть, менеджер — свои точки (сужает сервер), платформа — любая сеть
 * на выбор (решение владельца 2026-10-14). Сотруднику закрыто: это разбор работы смены.
 */
export const statsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/stats',
  component: lazyRouteComponent(() => import('./pages/StatsRoute'), 'StatsRoute'),
  staticData: {
    allowedRoles: ['platform_admin', 'client_admin', 'location_manager'],
  },
});

/**
 * Персонал: «Сотрудники точки» для менеджера и «Сотрудники сети» для админа.
 *
 * Один path на обе роли — экран сам решает объём колонок и полей. `employee` сюда
 * не ходит: он не ведёт ничей учёт, а список коллег с email ему не нужен.
 */
export const staffRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/staff',
  component: lazyRouteComponent(() => import('./pages/StaffRoute'), 'StaffRoute'),
  staticData: {
    allowedRoles: ['client_admin', 'location_manager'],
  },
});

/**
 * Кухонные экраны — ВИД КИОСКА, а не отдельная система.
 *
 * Раньше все трое жили вне защищённого поддерева: субъектом было устройство, у повара
 * не было ни email, ни пароля, и `requireAuth` выкинул бы кухню на `/login`, куда ей
 * нечего вводить.
 *
 * Отсюда росла дыра, ради которой всё и переделано: оба токена лежали на устройстве
 * рядом, и достаточно было снять `/kiosk` с адреса, чтобы из-под кухонного моноблока
 * открылся полный кабинет менеджера. Теперь маршруты под `appLayoutRoute`: без сессии
 * сюда не попасть, а с сессией меняется только ОТОБРАЖЕНИЕ — снятие `/kiosk` с адреса
 * приводит человека в его собственный кабинет, который ему и так открыт.
 *
 * Экран выбора сотрудника (`/kiosk/employee`) удалён целиком: карточек больше нет.
 *
 * `allowedRoles` у всех трёх НЕТ намеренно: киоск — вид отображения, и открыть его
 * вправе любая вошедшая роль. Владелец сети смотрит, как выглядит кухонный экран;
 * менеджер печатает сам, когда повар заболел. Ограничивать надо не вид, а данные —
 * это делает сервер (решение Д5: сотрудник видит только свои печати).
 */
export const kitchenLabelsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/kiosk/labels',
  component: lazyRouteComponent(() => import('./pages/KitchenLabelsRoute'), 'KitchenLabelsRoute'),
});

/** Экран печати — центральный поток продукта. */
export const printRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/kiosk/print',
  component: lazyRouteComponent(() => import('./pages/PrintRoute'), 'PrintRoute'),
});

/**
 * Настройка принтера (T-031). Как и печать — вид киоска, `allowedRoles` нет:
 * настраивает тот, кто стоит у принтера, а это повар. Данные не серверные (D2),
 * ограничивать нечего.
 */
export const printerSetupRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/kiosk/printer',
  component: lazyRouteComponent(() => import('./pages/PrinterSetupRoute'), 'PrinterSetupRoute'),
});

export type AlertsSearch = {
  /** Точка ленты, если человеку видно больше одной; нет — основная. */
  location?: string;
  /** Окно настроек уведомлений поверх ленты — через адрес (app.md §4). */
  modal?: 'settings';
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Лента предупреждений точки «как чат» (T-047). Как и остальные кухонные экраны —
 * без `allowedRoles`: вид открыт любой вошедшей роли, данные ограничивает сервер.
 */
export const alertsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/kiosk/alerts',
  validateSearch: (search: Record<string, unknown>): AlertsSearch => ({
    ...(typeof search.location === 'string' && UUID.test(search.location)
      ? { location: search.location }
      : {}),
    ...(search.modal === 'settings' ? { modal: 'settings' as const } : {}),
  }),
  component: lazyRouteComponent(() => import('./pages/AlertsRoute'), 'AlertsRoute'),
});

/**
 * «Точки сети» — список точек и создание новой вместе с меню.
 *
 * Смотреть могут все, у кого есть точки: менеджеру список нужен для переключения,
 * сотруднику — чтобы понимать, где он. Создание закрыто ролью на СЕРВЕРЕ, а экран
 * просто не рисует кнопку — нужны оба рубежа (app.md §7).
 */
export const locationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/locations',
  component: lazyRouteComponent(() => import('./pages/LocationsRoute'), 'LocationsRoute'),
  staticData: {
    // `employee` сюда не ходит. Раньше открытость объяснялась тем, что «сотруднику
    // список нужен, чтобы понимать, где он», — но свою точку он видит в собственном
    // профиле, а СПИСОК ТОЧЕК СЕТИ это управление, а не ориентация.
    allowedRoles: ['client_admin', 'location_manager'],
  },
});

/**
 * «Устройства» — раздача и отзыв права печати.
 *
 * Уровень сети: завести устройство значит раздать право печати, а отозвать —
 * остановить кухню. Менеджер точки сюда не ходит.
 */

/**
 * Дашборд мониторинга — стартовый экран менеджера точки и вторая половина продукта.
 *
 * `client_admin` видит те же данные в разрезе своих точек: владелец сети — ЛПР
 * по деньгам, и если он не открывает ни одного экрана мониторинга, платящее лицо
 * не видит половину продукта, за которую платит. `employee` сюда не ходит — у него
 * свой упрощённый список на моноблоке.
 */
export const monitoringRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/monitoring',
  component: lazyRouteComponent(() => import('./pages/MonitoringRoute'), 'MonitoringRoute'),
  staticData: {
    allowedRoles: ['client_admin', 'location_manager'],
  },
});

/**
 * Раздел Платформа-Админа (T-038): продуктовая аналитика по всем сетям.
 *
 * Только платформа: это единственное место, где видны сразу все клиенты, и сервер
 * отвечает на ручку 403 любой другой роли. Скрытие пункта меню этого не заменяет.
 */
export const platformRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/platform',
  component: lazyRouteComponent(
    () => import('./pages/PlatformAnalyticsRoute'),
    'PlatformAnalyticsRoute',
  ),
  staticData: {
    allowedRoles: ['platform_admin'],
  },
});

/** Сети платформы (T-042): список и карточка — вместо Django-админки. */
export const platformTenantsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/platform/tenants',
  component: lazyRouteComponent(
    () => import('./pages/PlatformTenantsRoute'),
    'PlatformTenantsRoute',
  ),
  staticData: { allowedRoles: ['platform_admin'] },
});

export const platformTenantRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/platform/tenants/$tenantId',
  component: lazyRouteComponent(() => import('./pages/PlatformTenantRoute'), 'PlatformTenantRoute'),
  staticData: { allowedRoles: ['platform_admin'] },
});

/** Релизы (T-042): что раздаётся устройствам и откат без доступа к серверу. */
export const platformReleasesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/platform/releases',
  component: lazyRouteComponent(
    () => import('./pages/PlatformReleasesRoute'),
    'PlatformReleasesRoute',
  ),
  staticData: { allowedRoles: ['platform_admin'] },
});

/** Справочник категорий сроков: смотреть могут все, вести — админ сети (T4A). */
export const categoriesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/catalog/categories',
  component: lazyRouteComponent(() => import('./pages/CategoriesRoute'), 'CategoriesRoute'),
  staticData: {
    // Справочник сроков ведут админ сети и менеджер точки. Сотрудник видит категорию
    // строкой в меню печати — там она и нужна, — а сам справочник ему нечем применить.
    allowedRoles: ['client_admin', 'location_manager'],
  },
});

export const routeTree = rootRoute.addChildren([
  setupRoute,
  loginRoute,
  inviteRoute,
  inviteCapturedRoute,
  appLayoutRoute.addChildren([
    startRoute,
    // Вид киоска — те же три экрана под той же сессией. Отдельного поддерева
    // у них больше нет: киоск это способ отображения, а не система входа.
    printRoute,
    printerSetupRoute,
    kitchenLabelsRoute,
    alertsRoute,
    locationMenuRoute,
    masterCatalogRoute,
    categoriesRoute,
    staffRoute,
    journalRoute,
    statsRoute,
    locationsRoute,
    monitoringRoute,
    platformRoute,
    platformTenantsRoute,
    platformTenantRoute,
    platformReleasesRoute,
  ]),
]);
